/**
 * BranchSwitcher — admin sidebar dropdown to filter by branch.
 * Admin: sees all branches + "Tất cả cơ sở" option.
 * Manager: hidden (scope fixed server-side).
 */
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useQueryClient } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import { useBranches } from '@client/lib/queries/use-branches';
import { useBranchFilter } from '@client/store/branch-filter';

interface BranchSwitcherProps {
  role: string;
}

export function BranchSwitcher({ role }: BranchSwitcherProps) {
  const qc = useQueryClient();
  const { selectedBranchId, setSelectedBranchId } = useBranchFilter();

  // Only admin sees the switcher
  if (role !== 'admin') return null;

  // Fetch all active branches for the dropdown list
  const { data } = useBranches({ status: 'active', pageSize: 100 });
  const branches = data?.items ?? [];

  function handleChange(value: string) {
    const next = value === '__all__' ? null : value;
    setSelectedBranchId(next);
    // Invalidate all queries so every list re-fetches with new branchId
    void qc.invalidateQueries();
  }

  return (
    <div className="px-2 pb-2">
      <Select
        value={selectedBranchId ?? '__all__'}
        onValueChange={handleChange}
      >
        <SelectTrigger className="w-full bg-sidebar-accent border-sidebar-border text-sidebar-foreground h-9 text-sm focus:ring-sidebar-ring">
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="h-4 w-4 shrink-0 text-sidebar-foreground/70" />
            <SelectValue placeholder="Chọn cơ sở" />
          </div>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">Tất cả cơ sở</SelectItem>
          {branches.map((b) => (
            <SelectItem key={b.id} value={b.id}>
              {b.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
